import type { Dispatch, SetStateAction } from 'react';
import type { ClientDecision } from '@ooh/ui/components/client-offer-view';
import {
  ClientOfferReviewConfirmationModals,
  ClientOfferReviewLightboxModal,
  type ClientOfferReviewModalKind,
  type ClientOfferReviewUnit,
} from '../client-offer-review';
import type { ClientOfferReviewLabelBundle } from './lib/client-offer-review-labels';
import {
  selectableForAcceptAll,
  type computeOfferReviewAggregates,
} from './lib/client-offer-review-shell-model';

type Lightbox = { id: string; idx: number } | null;

interface ClientOfferReviewShellDialogsProps {
  modal: ClientOfferReviewModalKind;
  onModalChange: (modal: ClientOfferReviewModalKind) => void;
  units: ClientOfferReviewUnit[];
  aggregates: ReturnType<typeof computeOfferReviewAggregates>;
  versionNumber: number;
  onDecisionChange: (itemId: string, decision: ClientDecision) => void;
  onAccept: () => void;
  lightbox: Lightbox;
  lightboxUnit: ClientOfferReviewUnit | null;
  onLightboxChange: Dispatch<SetStateAction<Lightbox>>;
  labels: ClientOfferReviewLabelBundle;
}

export function ClientOfferReviewShellDialogs({
  modal,
  onModalChange,
  units,
  aggregates,
  versionNumber,
  onDecisionChange,
  onAccept,
  lightbox,
  lightboxUnit,
  onLightboxChange,
  labels,
}: ClientOfferReviewShellDialogsProps) {
  return (
    <>
      <ClientOfferReviewConfirmationModals
        modal={modal}
        onClose={() => onModalChange(null)}
        // Reflects the actual "select all" outcome (excludes canAccept: false units),
        // not the raw available count, so the confirm dialog's body text matches what
        // clicking confirm will actually accept.
        availCount={selectableForAcceptAll(aggregates.avail).length}
        accCount={aggregates.accU.length}
        rejCount={aggregates.rejU.length}
        pendCount={aggregates.pendU.length}
        accValue={aggregates.accValue}
        versionNumber={versionNumber}
        onConfirmSelectAll={() => {
          for (const unit of selectableForAcceptAll(aggregates.avail)) {
            onDecisionChange(unit.id, 'accepted');
          }
          onModalChange(null);
        }}
        onConfirmClearAll={() => {
          for (const unit of units) onDecisionChange(unit.id, null);
          onModalChange(null);
        }}
        onConfirmAccept={() => {
          onModalChange(null);
          onAccept();
        }}
        labels={labels.confirmationModals}
      />

      {lightbox && lightboxUnit ? (
        <ClientOfferReviewLightboxModal
          unit={lightboxUnit}
          idx={lightbox.idx}
          onClose={() => onLightboxChange(null)}
          onPrev={() => onLightboxChange((prev) => (prev ? { ...prev, idx: prev.idx - 1 } : prev))}
          onNext={() => onLightboxChange((prev) => (prev ? { ...prev, idx: prev.idx + 1 } : prev))}
          labels={labels.lightbox}
        />
      ) : null}
    </>
  );
}
